import * as Sentry from '@sentry/react';
import { fetchAuthSession, fetchUserAttributes, signOut as amplifySignOut } from 'aws-amplify/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiErrorLogger } from '@/lib/monitoring/api-logger';
import * as Logger from '@/lib/monitoring/logger';
import { AuthTokenHolder } from '@/services/internal-api/core/auth-token-holder';
import type { EnhancedCognitoUser } from '@/store/app/types/use-store-types';

import { AUTH_STATE } from '@/contexts/auth/types/auth-context-enums';
import type { AuthContextActions, AuthContextState, AuthContextValue } from '@/contexts/auth/types/auth-context-types';
import { AuthContext } from '@/contexts/auth/auth-context';

const REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 minutes
const MAX_MOUNT_RETRIES = 3;
const RETRY_DELAYS_MS = [1000, 2000, 4000]; // Exponential backoff

enum REFRESH_TRIGGER {
  mount = 'mount',
  interval = 'interval',
  visibility = 'visibility',
  pageshow = 'pageshow',
  token_holder = 'token_holder',
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const initialState: AuthContextState = {
  authState: AUTH_STATE.LOADING,
  token: null,
  user: null,
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthContextState>(initialState);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  // Track if sign-out was requested to ignore in-flight refresh results
  const signOutRequestedRef = useRef(false);
  // Track in-progress refresh to serialize concurrent calls
  const refreshInProgressRef = useRef<Promise<string | null> | null>(null);
  // Track consecutive refresh failures for backoff
  const consecutiveFailuresRef = useRef(0);
  const MAX_CONSECUTIVE_FAILURES = 3;

  const clearRefreshInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const transitionTo = useCallback((authState: AUTH_STATE, token?: string | null, user?: EnhancedCognitoUser | null) => {
    if (!isMountedRef.current) return;
    setState(s => ({
      ...s,
      authState,
      ...(token !== undefined ? { token } : {}),
      ...(user !== undefined ? { user } : {}),
    }));
  }, []);

  const refreshToken = useCallback(async (
    trigger: REFRESH_TRIGGER = REFRESH_TRIGGER.token_holder,
  ): Promise<string | null> => {
    if (signOutRequestedRef.current) {
      return null;
    }

    if (refreshInProgressRef.current) {
      return refreshInProgressRef.current;
    }

    if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
      return null;
    }

    const doRefresh = async (): Promise<string | null> => {
      const refreshStartTime = performance.now();
      try {
        const session = trigger === REFRESH_TRIGGER.mount
          ? await fetchAuthSession()
          : await fetchAuthSession({ forceRefresh: true });
        const newToken = session?.tokens?.idToken?.toString() ?? null;

        if (signOutRequestedRef.current) {
          return null;
        }

        if (!newToken) {
          consecutiveFailuresRef.current += 1;
          apiErrorLogger('Token refresh returned no token', 'AuthProvider', null);
          Sentry.addBreadcrumb({
            category: 'auth',
            level: 'warning',
            message: 'Token refresh failed',
            data: {
              trigger,
              errorName: 'NoIdToken',
            },
          });

          if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
            Logger.warn('[Auth] Max refresh failures reached', {
              failures: consecutiveFailuresRef.current,
            });
            Sentry.addBreadcrumb({
              category: 'auth',
              level: 'warning',
              message: 'Auth sign-out triggered after failed refreshes',
              data: {
                trigger,
                consecutiveFailures: consecutiveFailuresRef.current,
              },
            });
            clearRefreshInterval();
            AuthTokenHolder.setToken(null);
            transitionTo(AUTH_STATE.UNAUTHENTICATED, null, null);
          }
          return null;
        }

        consecutiveFailuresRef.current = 0;
        AuthTokenHolder.setToken(newToken);

        setState(s => {
          if (s.authState === AUTH_STATE.UNAUTHENTICATED) {
            return s;
          }
          return {
            ...s,
            authState: AUTH_STATE.AUTHENTICATED,
            token: newToken,
          };
        });

        const durationMs = Math.round(performance.now() - refreshStartTime);
        Logger.debug('[Perf] Token refreshed', { durationMs });
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'info',
          message: 'Token refresh succeeded',
          data: { trigger },
        });

        return newToken;
      } catch (error: unknown) {
        if (signOutRequestedRef.current) {
          return null;
        }

        const err = (typeof error === 'object' && error !== null)
          ? error as { name?: string; code?: string; message?: string }
          : { name: undefined, code: undefined, message: String(error) };
        const isExpiredRefreshToken =
          err.name === 'NotAuthorizedException' ||
          err.message?.includes('Refresh Token has expired');

        if (isExpiredRefreshToken) {
          apiErrorLogger(
            'Refresh token has expired - user needs to re-authenticate',
            'AuthProvider',
            error
          );
          Sentry.addBreadcrumb({
            category: 'auth',
            level: 'info',
            message: 'Refresh token expired',
            data: { trigger },
          });
          clearRefreshInterval();
          transitionTo(AUTH_STATE.UNAUTHENTICATED, null, null);
        } else {
          consecutiveFailuresRef.current += 1;
          apiErrorLogger('Failed to refresh token', 'AuthProvider', error);
          Sentry.addBreadcrumb({
            category: 'auth',
            level: 'warning',
            message: 'Token refresh failed',
            data: {
              trigger,
              errorName: err.name,
              errorCode: err.code,
            },
          });

          if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
            Logger.warn('[Auth] Max refresh failures reached', {
              error,
              failures: consecutiveFailuresRef.current,
            });
            Sentry.addBreadcrumb({
              category: 'auth',
              level: 'warning',
              message: 'Auth sign-out triggered after failed refreshes',
              data: {
                trigger,
                consecutiveFailures: consecutiveFailuresRef.current,
              },
            });
            clearRefreshInterval();
            AuthTokenHolder.setToken(null);
            transitionTo(AUTH_STATE.UNAUTHENTICATED, null, null);
          }
        }

        return null;
      }
    };

    refreshInProgressRef.current = doRefresh().finally(() => {
      refreshInProgressRef.current = null;
    });

    return refreshInProgressRef.current;
  }, [transitionTo, clearRefreshInterval]);

  const signOut = useCallback(async (): Promise<void> => {
    // Mark sign-out as requested to prevent in-flight refreshes from re-authenticating
    signOutRequestedRef.current = true;

    try {
      clearRefreshInterval();
      await amplifySignOut();
    } catch (error) {
      Logger.error('Error during signOut:', error instanceof Error ? error : null, { error });

    } finally {
      transitionTo(AUTH_STATE.UNAUTHENTICATED, null, null);
      // Reset after transition to allow future sign-ins
      signOutRequestedRef.current = false;
    }
  }, [clearRefreshInterval, transitionTo]);

  const setAuthenticatedSession = useCallback((token: string, user?: EnhancedCognitoUser) => {
    // Sync token to AuthTokenHolder BEFORE state update
    // This ensures API calls made immediately after have access to the token
    AuthTokenHolder.setToken(token);
    transitionTo(AUTH_STATE.AUTHENTICATED, token, user ?? null);
  }, [transitionTo]);

  // Session check on mount with retry for transient errors
  useEffect(() => {
    let cancelled = false;
    let retryCount = 0;
    const sessionCheckStartTime = performance.now();

    const isTransientError = (error: unknown): boolean => {
      const err = error as { message?: string; name?: string };
      if (err.name === 'NetworkError' || err.message?.includes('Network')) return true;
      if (err.message?.includes('timeout') || err.message?.includes('Timeout')) return true;
      if (err.message?.includes('Failed to fetch')) return true;
      const isNoSession =
        err.message === 'No current user' ||
        err.name === 'NotAuthorizedException' ||
        err.name === 'UserUnAuthenticatedException';
      return !isNoSession;
    };

    const delay = async (ms: number): Promise<void> => {
      await new Promise(resolve => setTimeout(resolve, ms));
    };

    const fetchUserAttributesOrNull = async (): Promise<EnhancedCognitoUser | null> => {
      try {
        const attributes = await fetchUserAttributes();
        return attributes as unknown as EnhancedCognitoUser;
      } catch {
        Logger.warn('[Auth] Failed to fetch user attributes on mount');
        return null;
      }
    };

    const recoverSessionCheckAfterDelay = async (delayMs: number): Promise<void> => {
      await delay(delayMs);
      if (!cancelled) {
        return checkSession();
      }
    };

    const checkSession = async (): Promise<void> => {

      try {
        const userAttributesPromise = fetchUserAttributesOrNull();
        const session = await fetchAuthSession();
        if (cancelled) return;

        const token = session?.tokens?.idToken?.toString() ?? null;

        if (!token) {
          const durationMs = Math.round(performance.now() - sessionCheckStartTime);
          Logger.debug('[Perf] Auth no session', { durationMs });
          transitionTo(AUTH_STATE.UNAUTHENTICATED, null, null);
          return;
        }

        const user = await userAttributesPromise;
        if (cancelled) return;
        transitionTo(AUTH_STATE.AUTHENTICATED, token, user);

        const durationMs = Math.round(performance.now() - sessionCheckStartTime);
        Logger.debug('[Perf] Auth session restored', {
          durationMs,
          hasUser: !!user,
          retries: retryCount,
        });
      } catch (error: unknown) {
        if (cancelled) return;

        const err = error as { name?: string; message?: string };
        const isNoSession =
          err.message === 'No current user' ||
          err.name === 'NotAuthorizedException' ||
          err.name === 'UserUnAuthenticatedException';

        if (isNoSession) {
          const durationMs = Math.round(performance.now() - sessionCheckStartTime);
          Logger.debug('[Perf] Auth no session', { durationMs });
          transitionTo(AUTH_STATE.UNAUTHENTICATED, null, null);
          return;
        }

        if (isTransientError(error) && retryCount < MAX_MOUNT_RETRIES) {
          const delayMs = RETRY_DELAYS_MS[retryCount] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
          retryCount++;
          
          Logger.warn('[Auth] Session check failed, retrying', { 
            error,
            attempt: retryCount, 
            maxAttempts: MAX_MOUNT_RETRIES,
          });
          
          transitionTo(AUTH_STATE.RECONNECTING);
          
          return recoverSessionCheckAfterDelay(delayMs);
        }

        const durationMs = Math.round(performance.now() - sessionCheckStartTime);
        Logger.warn('[Perf] Auth session check failed', { error, durationMs, retries: retryCount });
        apiErrorLogger('Session check failed on mount', 'AuthProvider', error);
        transitionTo(AUTH_STATE.UNAUTHENTICATED, null, null);
      }
    };

    void checkSession();

    return () => {
      cancelled = true;
    };
  }, [transitionTo]);

  // Token refresh interval — runs only when authenticated.
  // Cleanup uses clearInterval(id) directly so static analyzers see the release path.
  // Transitioning away from authenticated still runs the previous effect's cleanup.
  useEffect(() => {
    if (state.authState !== AUTH_STATE.AUTHENTICATED) {
      return;
    }

    const intervalId = setInterval(() => {
      void refreshToken(REFRESH_TRIGGER.interval);
    }, REFRESH_INTERVAL_MS);
    intervalRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      if (intervalRef.current === intervalId) {
        intervalRef.current = null;
      }
    };
  }, [state.authState, refreshToken]);

  const handleWakeUp = useCallback((trigger: REFRESH_TRIGGER) => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    Sentry.addBreadcrumb({
      category: 'auth',
      level: 'info',
      message: 'Auth wake-up triggered',
      data: { reason: trigger },
    });
    Logger.debug('[Auth] Wake-up refresh triggered by visibility/pageshow');
    void refreshToken(trigger);
  }, [refreshToken]);

  // Proactive token refresh on tab visibility change and bfcache restore
  useEffect(() => {
    if (state.authState !== AUTH_STATE.AUTHENTICATED) {
      return;
    }
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const onVisibility = () => handleWakeUp(REFRESH_TRIGGER.visibility);
    const onPageshow = () => handleWakeUp(REFRESH_TRIGGER.pageshow);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageshow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageshow);
    };
  }, [state.authState, handleWakeUp]);

  // Track mount state for safe async updates
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync token to AuthTokenHolder for non-React code (API utils, interceptors)
  useEffect(() => {
    AuthTokenHolder.setToken(state.token);
  }, [state.token]);

  // Clear AuthTokenHolder only on provider unmount (not on every token change)
  useEffect(() => () => {
      AuthTokenHolder.setToken(null);
    }, []);

  // Set refresh callback for AuthTokenHolder on mount
  useEffect(() => {
    AuthTokenHolder.setRefreshCallback(refreshToken);
    return () => {
      AuthTokenHolder.setRefreshCallback(null);
    };
  }, [refreshToken]);

  const actions: AuthContextActions = useMemo(() => ({
    signOut,
    refreshToken,
    setAuthenticatedSession,
  }), [signOut, refreshToken, setAuthenticatedSession]);

  const contextValue = useMemo<AuthContextValue>(() => ({
    state,
    actions,
  }), [state, actions]);

  return (
    <AuthContext value={contextValue}>
      {children}
    </AuthContext>
  );
}
